import {
    XyDataSeries,
    HorizontalLineAnnotation,
    ELabelPlacement,
    EAnnotationLayer,
    SciChartSurface,
    ZoomPanModifier,
    MouseWheelZoomModifier,
    ZoomExtentsModifier,
    RolloverModifier,
    RubberBandXyZoomModifier,
    EXyDirection,
    EExecuteOn,
    LegendModifier,
    EAutoRange,
    NumberRange,
    EAxisAlignment,
    NumericAxis,
    DateTimeNumericAxis,
    CursorModifier,
    FastLineRenderableSeries,
    FastImpulseRenderableSeries,
    ISciChartSurfaceBase,
    UpdateSuspender,
} from "scichart";
import { IInitResult, TInitFunction } from "scichart-react";
import { enMessages } from "../../../../../messages/en-US";
import moment from "moment";
import { axisLabelDateFormatter } from "../../../../Shared/SciChart/SmartAxis/axisLabelDateFormatter";
import { GetChartTheme } from "../../../../Shared/SciChart/ChartConfiguration";
import { ColumnPaletteProvider } from "../PaletteProvider";
import { LocalizationService } from "@progress/kendo-react-intl";
import { DateTickProvider } from "../../../../Shared/SciChart/SmartAxis/DateTickProvider";
import { SmartDateTZLabelProvider } from "../../../../Shared/SciChart/SmartAxis/SmartDateTZLabelProvider";
import { createDateTzConverter } from "../../../../Shared/SciChart/SmartAxis/timezones";
import { arrayMax } from "../../../../Shared/Utilities/ArrayUtilities";

export interface TActivityChartConfigResult<TSurface extends ISciChartSurfaceBase> extends IInitResult<TSurface> {
    setAutoRange: (value: boolean) => void,
    changeChartType: (viewType: string) => void
}

export type TActivityConfigFunc = TInitFunction<SciChartSurface, TActivityChartConfigResult<SciChartSurface>>;

export interface Props {
    chartData: any;
    isSimpleChart?: boolean;
}

export interface ChartOptions {
    isAutoRange: boolean;
    viewType: string;
    visibleRange: { min: number | undefined; max: number | undefined; };
    setVisibleRange: (range: any) => void;
    setVisibleSeriesActivityFlowRate: (series: any, isChecked: boolean) => void;
    visibleSeriesActivityFlowRate: { [key: string]: boolean;}
    aggregator: string;
    isSimpleChart: boolean;
    isFullscreen?: boolean;
}

export const getChartAPI = (props:Props, chartOptions:ChartOptions, localizationService: LocalizationService, currentLanguage: { locale: string; localeId: string; }) => {
    if (!props.chartData || !props.chartData.dateFilter) {
        throw new Error("chartData or dateFilter is missing");
    }

    const endFilterDateUnix = moment(props.chartData.dateFilter.endDate).unix();
    const startFilterDateUnix = moment(props.chartData.dateFilter.startDate).unix();
    const theme = GetChartTheme();  
    
    let setAutoRangeCallback: (arg0: boolean) => void;
    let changeChartTypeCallback: (arg0: string) => void;

    const configAfterInit = (initResult) => {
        setAutoRangeCallback = initResult.setAutoRange;
        changeChartTypeCallback = initResult.changeChartType;
    };

    const setAutoRange = (value: boolean) => {
        if (setAutoRangeCallback !== undefined) {
            setAutoRangeCallback(value);
        }
    };

    const changeChartType = (viewType: string) => {
        if (changeChartTypeCallback !== undefined) {
            changeChartTypeCallback(viewType);
        }
    };

    const initChart: TActivityConfigFunc = async (rootElement: string | HTMLDivElement) => {
        const {sciChartSurface, wasmContext} = await SciChartSurface.create(rootElement, {theme: theme});

        // Validate data
        if (!props.chartData) {
            return { sciChartSurface: sciChartSurface, setAutoRange: () => {}, changeChartType: () => {} };
        }

        // Get data arrays with safe defaults
        const timestamps = (props.chartData.timestamps && Array.isArray(props.chartData.timestamps)) ? props.chartData.timestamps : [];
        const volumes = (props.chartData.volume && Array.isArray(props.chartData.volume)) ? props.chartData.volume : [];
        const flowRates = (props.chartData.flowRate && Array.isArray(props.chartData.flowRate)) ? props.chartData.flowRate : [];

        // If no data at all, return empty chart
        if (timestamps.length === 0 && volumes.length === 0 && flowRates.length === 0) {
            return { sciChartSurface: sciChartSurface, setAutoRange: () => {}, changeChartType: () => {} };
        }

        // Setup X-Axis
        const xAxis = new DateTimeNumericAxis(wasmContext, { 
            drawMajorBands: false, 
            drawMinorGridLines: false, 
            drawMajorGridLines: false, 
            visibleRange: new NumberRange(chartOptions.visibleRange?.min, chartOptions.visibleRange?.max),
            visibleRangeLimit: new NumberRange(startFilterDateUnix, endFilterDateUnix),
            growBy: new NumberRange(0.05, 0.05),
            labelStyle: {fontFamily:"Roboto", fontSize:12}
        });

        xAxis.deltaCalculator.getDeltaFromRange = axisLabelDateFormatter.getDeltaFromRange;
        const timezone = moment.tz.guess();
        xAxis.tickProvider = new DateTickProvider(wasmContext, timezone);
        xAxis.labelProvider = new SmartDateTZLabelProvider(timezone, true, currentLanguage?.localeId);
        const dateTzConverter = createDateTzConverter(timezone);
        xAxis.labelProvider.formatCursorLabel = timestamp =>
            chartOptions.aggregator === 'none' 
                ? axisLabelDateFormatter.toFullDateTime(timestamp, dateTzConverter, currentLanguage?.localeId) 
                : axisLabelDateFormatter.toFullDateOnly(timestamp, dateTzConverter, currentLanguage?.localeId);
        xAxis.visibleRangeChanged.subscribe((args) => { chartOptions.setVisibleRange(args?.visibleRange); });
        sciChartSurface.xAxes.add(xAxis);

        // Setup Y-Axis for Volume (Right)
        const yAxisVolume = new NumericAxis(wasmContext, {
            id: "yAxisVolume",
            axisAlignment: EAxisAlignment.Right, 
            autoRange: chartOptions.isAutoRange ? EAutoRange.Always : EAutoRange.Never,
            drawMajorBands: false,
            drawMinorGridLines: false, 
            drawMajorGridLines: true, 
            labelPostfix: " L", 
            labelPrecision: 2,
            growBy: new NumberRange(0, 0.2),
            labelStyle: {fontFamily:"Roboto", fontSize:12}
        });

        yAxisVolume.visibleRangeChanged.subscribe(args => {
            if (args?.visibleRange && args.visibleRange.min !== 0) {
                yAxisVolume.visibleRange = new NumberRange(0, args.visibleRange.max);
            }
        });
        sciChartSurface.yAxes.add(yAxisVolume);

        // Setup Y-Axis for Flow Rate (Left)
        const yAxisFlowRate = new NumericAxis(wasmContext, {
            axisAlignment: EAxisAlignment.Left, 
            autoRange: chartOptions.isAutoRange ? EAutoRange.Always : EAutoRange.Never,
            drawMajorBands: false,
            drawMinorGridLines: false, 
            drawMajorGridLines: true, 
            labelPostfix: " L/min", 
            labelPrecision: 2,
            growBy: new NumberRange(0, 0.2),
            labelStyle: {fontFamily:"Roboto", fontSize:12}
        });

        yAxisFlowRate.visibleRangeChanged.subscribe(args => {
            if (args?.visibleRange && args.visibleRange.min !== 0) {
                yAxisFlowRate.visibleRange = new NumberRange(0, args.visibleRange.max);
            }
        });
        sciChartSurface.yAxes.add(yAxisFlowRate);

        // Setup Chart Modifiers
        const legend = new LegendModifier({
            placementDivId: chartOptions.isFullscreen ? "legend-activity-flowRate-fullscreen" : "legend-activity-flowRate",
            showCheckboxes: true,
            showSeriesMarkers: true,
            showLegend: chartOptions.isFullscreen ? true : !chartOptions.isSimpleChart,
            isCheckedChangedCallback(series, isChecked) {
                chartOptions.setVisibleSeriesActivityFlowRate(series.id, isChecked);
            },
        });
        sciChartSurface.chartModifiers.add(legend);

        // Tooltip template
        const tooltipTemplate = (seriesInfo: any) => {
            if (!seriesInfo) return [];
            const lines: string[] = [];
            if (seriesInfo.seriesName) lines.push(seriesInfo.seriesName);
            if (seriesInfo.formattedXValue) lines.push(`x: ${seriesInfo.formattedXValue}`);
            if (seriesInfo.formattedYValue) lines.push(`y: ${seriesInfo.formattedYValue}`);
            return lines;
        };

        sciChartSurface.chartModifiers.add(
            new CursorModifier({ showAxisLabels: false, id: "ActivityModifierGroup" }),
            new ZoomPanModifier({xyDirection: EXyDirection.XDirection}),
            new MouseWheelZoomModifier({xyDirection: EXyDirection.XDirection}),
            new ZoomExtentsModifier({xyDirection: EXyDirection.XyDirection}),
            new RolloverModifier({
                hitTestRadius: 10,
                showTooltip: true,
                snapToDataPoint: true,
                showRolloverLine: false,
                tooltipDataTemplate: tooltipTemplate,
                id: "ActivityRollOver"
            }),
            new RubberBandXyZoomModifier({ executeOn: EExecuteOn.MouseRightButton }),
        );

        // Create Volume Series (Bar or Line based on viewType)
        const createVolumeSeries = (viewType: string) => {
            if (timestamps.length === 0 || volumes.length === 0) return null;

            const volumeDataSeries = new XyDataSeries(wasmContext, {
                xValues: timestamps, 
                yValues: volumes, 
                dataSeriesName: localizationService.toLanguageString('custom.volumeInL', enMessages.custom.volumeInL)
            });

            const isBar = viewType === 'bar' || viewType === 'userBar';
            
            if (isBar) {
                const volumeSeries = new FastImpulseRenderableSeries(wasmContext, {
                    stroke: "rgba(50, 53, 67,1)", 
                    fill: "rgba(50, 53, 67,1)", 
                    strokeThickness: 1,
                    paletteProvider: new ColumnPaletteProvider("rgba(50, 53, 67,1)"),
                    id: "VolumeId",
                    yAxisId: "yAxisVolume",
                    dataSeries: volumeDataSeries,
                    isVisible: chartOptions.visibleSeriesActivityFlowRate.VolumeId !== false
                });
                volumeSeries.rolloverModifierProps.tooltipTextColor = 'White';
                volumeSeries.rolloverModifierProps.showRollover = true;
                volumeSeries.rolloverModifierProps.markerColor = 'rgba(50, 53, 67,1)';
                return volumeSeries;
            } else {
                const volumeSeries = new FastLineRenderableSeries(wasmContext, {
                    stroke: "rgba(50, 53, 67,1)", 
                    id: "VolumeId",
                    yAxisId: "yAxisVolume",
                    dataSeries: volumeDataSeries,
                    isVisible: chartOptions.visibleSeriesActivityFlowRate.VolumeId !== false
                });
                volumeSeries.rolloverModifierProps.tooltipTextColor = 'White';
                volumeSeries.rolloverModifierProps.showRollover = true;
                volumeSeries.rolloverModifierProps.markerColor = 'rgba(50, 53, 67,1)';
                return volumeSeries;
            }
        };

        const volumeSeries = createVolumeSeries(chartOptions.viewType);
        if (volumeSeries) {
            sciChartSurface.renderableSeries.add(volumeSeries);
        }

        // Create Flow Rate Series (Line Chart only)
        if (timestamps.length > 0 && flowRates.length > 0) {
            const flowRateDataSeries = new XyDataSeries(wasmContext, {
                xValues: timestamps, 
                yValues: flowRates, 
                dataSeriesName: localizationService.toLanguageString('custom.flowRate', enMessages.custom.flowRate)
            });

            const flowRateSeries = new FastLineRenderableSeries(wasmContext, {
                stroke: "#00A651", 
                id: "FlowRateId",
                dataSeries: flowRateDataSeries,
                isVisible: chartOptions.visibleSeriesActivityFlowRate.FlowRateId !== false
            });

            flowRateSeries.rolloverModifierProps.tooltipTextColor = "white";
            flowRateSeries.rolloverModifierProps.showRollover = true;
            flowRateSeries.rolloverModifierProps.markerColor = "#00A651";
            sciChartSurface.renderableSeries.add(flowRateSeries);
        }

        // Add 0 line annotation
        const horizontalLine = new HorizontalLineAnnotation({
            labelPlacement: ELabelPlacement.Axis,
            stroke: 'grey',
            strokeThickness: 3,
            y1: 0,
            annotationLayer: EAnnotationLayer.BelowChart
        });
        sciChartSurface.annotations.add(horizontalLine);

        // Setup APIs
        const setAutoRange = (value: boolean) => {
            UpdateSuspender.using(sciChartSurface, () => {
                yAxisVolume.autoRange = value ? EAutoRange.Always : EAutoRange.Never;
                yAxisFlowRate.autoRange = value ? EAutoRange.Always : EAutoRange.Never;
                
                if (!value) {
                    if (Array.isArray(volumes) && volumes.length > 0) {
                        const validVolumes = volumes.filter(v => v != null && !isNaN(v) && typeof v === 'number');
                        if (validVolumes.length > 0) {
                            const maxVolume = arrayMax(validVolumes);
                            if (!isNaN(maxVolume) && maxVolume > 0) {
                                yAxisVolume.visibleRange = new NumberRange(0, maxVolume * 1.2);
                            }
                        }
                    }
                    if (Array.isArray(flowRates) && flowRates.length > 0) {
                        const validFlowRates = flowRates.filter(v => v != null && !isNaN(v) && typeof v === 'number');
                        if (validFlowRates.length > 0) {
                            const maxFlowRate = arrayMax(validFlowRates);
                            if (!isNaN(maxFlowRate) && maxFlowRate > 0) {
                                yAxisFlowRate.visibleRange = new NumberRange(0, maxFlowRate * 1.2);
                            }
                        }
                    }
                }
            });
        };
        setAutoRange(chartOptions.isAutoRange);

        const changeChartType = (viewType: string) => {
            UpdateSuspender.using(sciChartSurface, () => {
                const index = sciChartSurface.renderableSeries.asArray().findIndex(x => x.id === "VolumeId");
                if (index !== -1 && timestamps.length > 0 && volumes.length > 0) {
                    const oldSeries = sciChartSurface.renderableSeries.get(index);
                    const newVolumeSeries = createVolumeSeries(viewType);
                    if (newVolumeSeries && oldSeries) {
                        newVolumeSeries.isVisible = oldSeries.isVisible;
                        sciChartSurface.renderableSeries.set(index, newVolumeSeries);
                    }
                }
            });
        };

        return { sciChartSurface: sciChartSurface, setAutoRange, changeChartType};
    };

    return {initChart, configAfterInit, setAutoRange, changeChartType};
};
